import { ApolloQueryResult, MutationTuple, PureQueryOptions, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import {
  GetProductsDocument,
  GetProductDocument,
  GetVariantsDocument,
  GetParticipantsDocument,
  PurchaseDocument,
  SadminPurchaseDocument,
  CreateOrUpdateDocument,
  CreateVariantDocument,
  UpdateVariantDocument,
  RemoveVariantDocument,
  GetInventoryDocument,
} from '@/store/v2';
import { HookResult } from './apolloClient';
import { BaseProduct, Product, Space, Variant } from '@/types/learning/learning-catalog';
import { OrderDirection, Participant, ProductParticipantParams } from '@/types/product';
import { LearningStatus } from '@/types/learning';
import { REFETCH_COURSE_CATALOG } from './catalog';
import { FEATURE, useFeatureEnabled } from '@/feature-toggles';

interface ProductResponse extends HookResult {
  product: Product | undefined;
  refetch: (variables?: { id?: number }) => Promise<ApolloQueryResult<{ getProduct: Product }>>;
}

export const useProduct = (id: string | undefined): ProductResponse => {
  const { data, loading, refetch, networkStatus, error } = useQuery<{ getProduct: Readonly<Product> }>(GetProductDocument, {
    skip: !id || isNaN(parseInt(id)),
    variables: { id: id && parseInt(id) },
  });

  const product: Product | undefined = data?.getProduct || undefined;

  return {
    product,
    loading,
    refetch,
    error,
    networkStatus,
  };
};

type VariantWithId = Variant & { id: number };

interface VariantsResponse extends HookResult {
  variants: VariantWithId[];
  refetch: (variables?: { productId?: number }) => Promise<ApolloQueryResult<{ getVariants: VariantWithId[] }>>;
}

export const useVariants = (productId?: number): VariantsResponse => {
  const { data, loading, networkStatus, error, refetch } = useQuery<{ getVariants: VariantWithId[] }>(GetVariantsDocument, {
    skip: !productId,
    variables: { productId },
  });

  const variantsToMap = data?.getVariants || [];
  const variants = variantsToMap?.map((variant: Readonly<VariantWithId>) => {
    return {
      ...variant,
      validFrom: variant.validFrom ? dayjs(variant.validFrom).local().format('YYYY-MM-DD') : variant.validFrom,
      validTo: variant.validTo ? dayjs(variant.validTo).local().format('YYYY-MM-DD') : variant.validTo,
    };
  });

  return {
    variants,
    loading,
    networkStatus,
    error,
    refetch,
  };
};

interface ParticipantsResponse extends HookResult {
  participants: Participant[];
  total: number;
  totalPages: number;
  refetch: (variables?: {
    productId?: number;
    page?: number;
    pageSize?: number;
    orderDirection?: OrderDirection;
    stateIds?: number[];
  }) => Promise<ApolloQueryResult<{ getParticipants: { participants: Participant[]; total: number; totalPages: number } }>>;
}

export const useParticipants = ({ productId, ...rest }: ProductParticipantParams): ParticipantsResponse => {
  const { data, loading, refetch, networkStatus, error } = useQuery<{
    getParticipants: { participants: Participant[]; total: number; totalPages: number };
  }>(GetParticipantsDocument, {
    skip: !productId,
    variables: { productId, ...rest },
  });

  if (data?.getParticipants) {
    const { participants, total, totalPages } = data.getParticipants;

    return {
      participants,
      total,
      totalPages,
      loading,
      refetch,
      error,
      networkStatus,
    };
  }

  return {
    participants: [],
    total: 0,
    totalPages: 0,
    loading,
    refetch,
    error,
    networkStatus,
  };
};

export const usePurchaseProduct = (
  id?: number,
): MutationTuple<
  { purchase: { orderId: number } },
  { products: Partial<{ id: number; productVariantId: number }>[]; note?: string }
> => {
  const isCatalogJSONResponseEnabled = useFeatureEnabled(FEATURE.SYSTEM_CATALOG_GQL_JSON_RESPONSE_ENABLED);
  const refetchQueries: PureQueryOptions[] = [
    { query: GetInventoryDocument },
    REFETCH_COURSE_CATALOG(isCatalogJSONResponseEnabled),
  ];

  if (id) {
    refetchQueries.push({ query: GetProductDocument, variables: { id } });
  }

  return useMutation(PurchaseDocument, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
};

export const useSadminPurchaseProduct = (
  refetchQueries?: Array<string | PureQueryOptions>,
): MutationTuple<
  {
    sadminPurchase: {
      result: {
        orderId: number;
        email: string;
        message: string;
      }[];
      success: boolean;
      message: string;
      code: number;
    };
  },
  { product: { id: number; spaceId: number; productVariantId?: number }; emails: string[] }
> => useMutation(SadminPurchaseDocument, { refetchQueries });

export const useCreateOrUpdateProduct = (
  id?: number,
): MutationTuple<
  {
    createOrUpdate: {
      id: number;
    };
  },
  { product: Product }
> => {
  const refetchQueries: PureQueryOptions[] = [];
  if (id) {
    refetchQueries.push({ query: GetProductDocument, variables: { id } });
  }

  return useMutation(CreateOrUpdateDocument, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
};

export const useTransitionProductStatus = (): MutationTuple<
  {
    createOrUpdate: {
      id: number;
    };
  },
  { product: Product }
> =>
  useMutation(CreateOrUpdateDocument, {
    refetchQueries: [{ query: GetProductsDocument }, { query: GetInventoryDocument }],
    awaitRefetchQueries: true,
  });

export const useCreateVariant = (
  productId?: number,
): MutationTuple<
  {
    createVariant: {
      id: number;
    };
  },
  {
    productId: number;
    variant: Variant;
  }
> =>
  useMutation(CreateVariantDocument, {
    refetchQueries: [
      { query: GetVariantsDocument, variables: { productId } },
      { query: GetProductDocument, variables: { id: productId } },
    ],
    awaitRefetchQueries: true,
  });

export const useUpdateVariant = (
  productId?: number,
): MutationTuple<
  {
    updateVariant: {
      id: number;
    };
  },
  {
    productId: number;
    variant: Variant;
  }
> =>
  useMutation(UpdateVariantDocument, {
    refetchQueries: [
      { query: GetVariantsDocument, variables: { productId } },
      { query: GetProductDocument, variables: { id: productId } },
    ],
    awaitRefetchQueries: true,
  });

export const useRemoveVariant = (
  productId?: number,
): MutationTuple<
  {
    removeVariant: {
      value: string;
    };
  },
  {
    productId: number;
    variantId: number;
  }
> =>
  useMutation(RemoveVariantDocument, {
    refetchQueries: [
      { query: GetVariantsDocument, variables: { productId } },
      { query: GetProductDocument, variables: { id: productId } },
    ],
    awaitRefetchQueries: true,
  });

export function newProduct(spaces: readonly Space[]): BaseProduct {
  return {
    name: '',
    label: '',
    costCenter: '',
    contentOwner: undefined,
    legalEntity: '',
    price: 0,
    currency: '',
    status: LearningStatus.AVAILABLE,
    subjects: [],
    space: {
      id: spaces[0].id,
    },
    meta: {
      ATTRIBUTES: '{}',
      CONNECTIONS: '[]',
      IMAGE: null,
      TEASER: null,
      DESCRIPTION: null,
      FORMAT: null,
      LEVEL: null,
      LENGTH: null,
      EFFORT: null,
      TOTAL_EFFORT: null,
      LANGUAGE: 'English',
      DISCUSSION_SECTION: undefined,
      REFLECTION_SECTION: undefined,
      INSTITUTION: null,
      INSTRUCTORS: null,
      LOCATION: null,
      CATEGORY: null,
      SUBJECT: null,
      ENROLL_BUTTON_TEXT: null,
      CONFIRM_MESSAGE: null,
      PROVIDER: null,
      CUSTOM_LINK: null,
      OBJECT_ID: null,
    },
    flags: {
      available: false,
      approvalRequired: false,
      globalMandatoryTraining: false,
      enrollmentDisabled: false,
      socialFeaturesEnabled: false,
      reflectionsEnabled: false,
      requiresApplication: false,
      featured: false,
    },
    services: [],
  };
}
